import { ApprovalRefundEnum } from 'api-hooks/common';
import { PaymentMethodEnum } from 'api-hooks/payment-channel';
import {
  RefundModel,
  RefundMutationUpdateReleaseSaleInput,
  ReleaseSaleModel,
} from 'api-hooks/refund/refund';
import { NavigationRoutes } from 'common/constants';
import { RefundStatusEnum } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form } from 'components/elements';
import { Button } from 'components/elements/buttons';
import { FormState } from 'components/elements/form';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import TitleStatus from 'legacy-modules/common/title-status';
import useReleaseStatus from 'legacy-modules/refund/hooks/use-release-status';
import { FormDivider } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { useRefundFormState } from '../../../hooks/use-refund-formstate';
import { ReleaseSaleFormType } from '../form-type';
import ActivitySaleSection from '../refund-customer/activity';
import CruiseSaleSection from '../refund-customer/cruise';
import CustomerDetailField from '../refund-customer/customer-detail-field';
import CustomerPaymentField from '../refund-customer/customer-payment-field';
import DocumentSaleSection from '../refund-customer/document';
import FlightSaleSection from '../refund-customer/flight';
import HotelSaleSection from '../refund-customer/hotel';
import InformationCustomerField from '../refund-customer/information-customer-field';
import ProductSaleSection from '../refund-customer/product';
import TourSaleSection from '../refund-customer/tour';

interface Props {
  refund?: RefundModel;
  approvalRefundType?: ApprovalRefundEnum;
  onSubmit: (
    input: RefundMutationUpdateReleaseSaleInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<ReleaseSaleModel | undefined>;
}

export default function RefundCustomerForm(props: Props) {
  const { refund, approvalRefundType } = props;
  const { t } = useTranslation();
  const releaseSale = refund?.releaseSale;
  const defaultValues = React.useMemo<ReleaseSaleFormType>(() => {
    return {
      releaseSale: releaseSale
        ? {
            amount: releaseSale.amount,
            customerBillingId: releaseSale?.customerBilling?.id ?? null,
            customerId: releaseSale.customer.id,
            paymentableId: releaseSale.paymentable.chequeGiro?.id
              ? releaseSale.paymentable.chequeGiro?.id
              : releaseSale.paymentable.id,
            paymentMethod: releaseSale.paymentMethod.type,
            paymentMethodId: releaseSale.paymentMethod.id,
            refId: releaseSale.sale.refId,
            id: releaseSale.sale.id,
            chequeNote: {
              availableAt: releaseSale.paymentable.availableAt,
              bankAccountId: releaseSale.paymentable.bankAccount?.id,
              chequeGiroId: releaseSale.paymentable.chequeGiro?.id,
              expiredAt: releaseSale.paymentable.expiredAt,
              issuedAt: releaseSale.paymentable.issuedAt,
              referenceNumber: releaseSale.paymentable.referenceNumber,
            },
            //Information Additional
            adminId: releaseSale.sale.admin.id,
            operatorId: releaseSale.sale.operator?.id,
            salesmanId: releaseSale.sale.salesman?.id,
            code: releaseSale.sale.code || '',
            description: releaseSale.sale.description,
            transactionAt: releaseSale.sale.transactionAt,
            total: releaseSale.sale.total,
            transactionSaleType: releaseSale.sale.transactionType ?? '',

            previousSale: {
              customerBillingId: releaseSale?.customerBilling?.id ?? null,
              customerId: releaseSale.customer.id,
              adminId: releaseSale.sale.admin.id,
              operatorId: releaseSale.sale.operator?.id,
              salesmanId: releaseSale.sale.salesman?.id,
              code: releaseSale.sale.code || '',
              description: releaseSale.sale.description,
              transactionAt: releaseSale.sale.transactionAt,
              total: releaseSale.sale.total,
            },
          }
        : {
            id: null,
            amount: 0,
            chequeNote: null,
            customerBillingId: null,
            customerId: null,
            paymentableId: null,
            paymentMethodId: null,
            refId: null,

            //Information Additional
            adminId: null,
            code: '',
            description: '',
            operatorId: null,
            salesmanId: null,
            total: 0,
            transactionAt: null,
          },
    };
  }, [releaseSale]);

  const methods = useForm({
    defaultValues,
    mode: 'all',
  });

  const onSubmit = React.useCallback(
    async (values: ReleaseSaleFormType) => {
      try {
        const result = {
          ...values.releaseSale,
          paymentableId:
            values.releaseSale.paymentMethod === PaymentMethodEnum.Balance
              ? values.releaseSale.customerId
              : values.releaseSale.paymentableId,
        };

        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const {
    editableAgent,
    editableCustomer,
    editableInformation,
    setEditableForm,
  } = useRefundFormState();

  const { releaseStatus } = useReleaseStatus(releaseSale);

  const noEdit =
    !releaseStatus.isRequested ||
    approvalRefundType === ApprovalRefundEnum.Customer ||
    editableAgent ||
    editableInformation;

  const onChangeEditable = React.useCallback(
    ({ editable }: { editable: boolean }) => {
      if (editable) {
        setEditableForm({
          customer: editable,
          agent: false,
          information: false,
        });
      } else {
        setEditableForm({
          customer: editable,
          agent: editableAgent,
          information: editableInformation,
        });
      }
    },
    [editableAgent, editableInformation, setEditableForm],
  );

  const { navigate } = useNavigation();
  const permission = useGetCrudPermission('release-sales');
  const onNavigate = React.useCallback(() => {
    if (!permission.view) return;
    if (releaseSale?.id) {
      navigate(NavigationRoutes.RefundReleaseUserView, {
        id: releaseSale.id,
      });
    }
  }, [navigate, permission.view, releaseSale?.id]);

  const customViewName =
    refund && [t('common:refund'), refund.number].join(' - ');

  const { requestApprovalButton } = useApprovalAction(
    'release-sales',
    releaseSale,
  );
  const { resolverButton } = useResolveApproval('release-sales', releaseSale);
  const { revertDraftButton } = useRevertDraftAction(
    'release-sales',
    releaseSale,
  );

  const isActive = refund?.status === RefundStatusEnum.Processed;
  const onRenderRightComponent = React.useMemo(() => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {isActive && (
          <>
            {revertDraftButton}
            {requestApprovalButton}
            {resolverButton}
          </>
        )}
        <Button buttonType="secondary" onClick={onNavigate}>
          {t('refund:see_release')}
        </Button>
      </div>
    );
  }, [
    isActive,
    onNavigate,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
    t,
  ]);

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={editableCustomer}
      customViewName={customViewName}
      noReset
    >
      <FormState>
        {({ editable }) => {
          onChangeEditable({ editable });

          return (
            <FormHeader
              data={refund}
              onDelete={() => {}}
              noDelete
              title={
                <TitleStatus
                  type="release"
                  title={t('refund:customer_refund')}
                  status={refund!.releaseSale!.status}
                />
              }
              noEdit={noEdit}
              onRenderComponent={editable ? undefined : onRenderRightComponent}
            />
          );
        }}
      </FormState>
      <div>
        <FormState>
          {({ editable }) => (
            <InformationCustomerField
              refund={refund}
              key={editable ? 'enabled' : 'disabled'}
            />
          )}
        </FormState>
        <CustomerPaymentField />
        <CustomerDetailField />
        <FormState>
          {({ editable }) => (
            <React.Fragment key={editable ? 'enabled' : 'disabled'}>
              <FlightSaleSection refund={refund} />
              <HotelSaleSection refund={refund} />
              <CruiseSaleSection refund={refund} />
              <ActivitySaleSection refund={refund} />
              <DocumentSaleSection refund={refund} />
              <TourSaleSection refund={refund} />
              <ProductSaleSection refund={refund} />
            </React.Fragment>
          )}
        </FormState>
      </div>
      {!refund?.releasePurchase && <FormDivider />}
    </Form>
  );
}
